import { useMemo, useState } from "react";
import { ArrowLeft, Boxes, Plus, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useSchema } from "@/context/SchemaContext";
import { Icon } from "@/lib/icons";

const DEFAULT_COLORS = ["#22D3EE", "#A78BFA", "#34D399", "#F59E0B", "#F472B6", "#F97316"];
const DEFAULT_ICONS = ["Factory", "Server", "Bot", "Cloud", "ShoppingBag", "Globe"];

export default function FactoriesOverview() {
  const { factories, resources, refreshSchema } = useSchema();
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", desc: "", color: DEFAULT_COLORS[0], icon: DEFAULT_ICONS[0] });
  const [saving, setSaving] = useState(false);

  const list = useMemo(() => Object.values(factories).filter((factory) => factory.id !== "main"), [factories]);

  const totalResources = Object.values(resources).filter((resource) => resource.factory !== "admin").length;
  const totalIntegrations = list.reduce((sum, factory) => sum + (factory.integrations?.length ?? 0), 0);
  const activeFactories = list.length;

  const handleCreate = async (event) => {
    event.preventDefault();
    const trimmedName = draft.name.trim();
    if (!trimmedName || saving) return;

    try {
      setSaving(true);
      await api.post("/factories", {
        name: trimmedName,
        desc: draft.desc.trim() || "مصنع جديد يتلقى تكاملات ومهام تشغيلية داخل النظام.",
        color: draft.color,
        icon: draft.icon,
        integrations: [],
      });
      setDraft({ name: "", desc: "", color: DEFAULT_COLORS[0], icon: DEFAULT_ICONS[0] });
      setModalOpen(false);
      if (refreshSchema) await refreshSchema();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="factories-overview-page">
      <div className="px-4 sm:px-8 pt-6 pb-4 border-b border-white/[0.06]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 grid place-items-center text-cyan-300">
              <Boxes size={22} />
            </div>
            <div>
              <p className="text-[11px] tracking-wide text-slate-500">قسم المصانع</p>
              <h1 className="text-2xl font-bold text-slate-50">لوحة المصانع والفرق</h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-200 hover:bg-cyan-500/20 transition-colors"
            data-testid="create-factory-button"
          >
            <Plus size={15} /> إنشاء مصنع جديد
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        <div className="grid gap-3 md:grid-cols-3 mb-6">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <p className="text-[11px] text-slate-500">إجمالي المصانع</p>
            <p className="mt-2 text-2xl font-bold text-slate-50">{activeFactories}</p>
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <p className="text-[11px] text-slate-500">إجمالي الموارد</p>
            <p className="mt-2 text-2xl font-bold text-slate-50">{totalResources}</p>
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <p className="text-[11px] text-slate-500">إجمالي التكاملات</p>
            <p className="mt-2 text-2xl font-bold text-slate-50">{totalIntegrations}</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((factory) => {
            const relatedResources = Object.values(resources).filter((resource) => resource.factory === factory.id);
            const resourceCount = relatedResources.length;
            const integrationsCount = factory.integrations?.length ?? 0;

            return (
              <Link
                key={factory.id}
                to={`/factory/${factory.id}`}
                className="group rounded-2xl border border-white/[0.06] bg-[#0A1120]/80 p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.02)] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/10 hover:bg-[#0C1425]"
                data-testid={`factory-overview-card-${factory.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-12 w-12 rounded-xl border grid place-items-center"
                      style={{
                        color: factory.color,
                        background: `${factory.color}14`,
                        borderColor: `${factory.color}44`,
                        boxShadow: `0 0 25px -10px ${factory.color}`,
                      }}
                    >
                      <Icon name={factory.icon} size={20} />
                    </span>
                    <div>
                      <p className="text-[10px] text-slate-500">مصنع {factory.id.replace("f", "") || "إدارة"}</p>
                      <h2 className="text-lg font-semibold text-slate-50">{factory.name}</h2>
                    </div>
                  </div>
                  <Sparkles size={15} className="text-slate-500 group-hover:text-slate-200" />
                </div>

                <p className="mt-4 text-sm leading-6 text-slate-400">{factory.desc || "مصنع قابل للتشغيل مع موارد ومهام متكاملة."}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-[10px] text-slate-300">
                    {resourceCount} مورد
                  </span>
                  <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-[10px] text-slate-300">
                    {integrationsCount} تكامل
                  </span>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-white/[0.06] pt-3 text-sm text-slate-300">
                  <span>فتح المصنع</span>
                  <ArrowLeft size={15} className="transition-transform group-hover:-translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-[#0B1220] p-5 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xl font-semibold text-slate-50">إنشاء مصنع جديد</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-white/[0.08] p-2 text-slate-300 hover:text-white" aria-label="إغلاق">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreate} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm text-slate-300">اسم المصنع</label>
                <input
                  value={draft.name}
                  onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-slate-50 outline-none focus:border-cyan-400/40"
                  placeholder="مثال: مصنع الطلبات"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-slate-300">الوصف</label>
                <textarea
                  value={draft.desc}
                  onChange={(event) => setDraft((prev) => ({ ...prev, desc: event.target.value }))}
                  rows={3}
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-slate-50 outline-none focus:border-cyan-400/40"
                  placeholder="وصف موجز لوظائف المصنع"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm text-slate-300">اللون</label>
                  <div className="flex gap-2 flex-wrap">
                    {DEFAULT_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setDraft((prev) => ({ ...prev, color }))}
                        className={`h-8 w-8 rounded-full border-2 ${draft.color === color ? "border-white" : "border-transparent"}`}
                        style={{ background: color }}
                        aria-label={`اختر اللون ${color}`}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm text-slate-300">الأيقونة</label>
                  <select
                    value={draft.icon}
                    onChange={(event) => setDraft((prev) => ({ ...prev, icon: event.target.value }))}
                    className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-slate-50 outline-none focus:border-cyan-400/40"
                  >
                    {DEFAULT_ICONS.map((icon) => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="rounded-xl border border-white/[0.08] px-3 py-2 text-slate-300">إلغاء</button>
                <button type="submit" disabled={saving} className="rounded-xl bg-cyan-500 px-3 py-2 text-slate-950 font-semibold disabled:opacity-60">
                  {saving ? "جارٍ الحفظ..." : "حفظ المصنع"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
